import React, { useMemo, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../services/api";
import {
  labelTipo,
  normalizarProjetoBackend,
  normalizarTextoBusca,
  obterTipoProjeto,
} from "../utils/projetos";
import "../styles/admin.css";

function AdminPreview({ apresentacao, titulo }) {
  if (!apresentacao?.arquivo) return <div className="admin-card-thumb vazio" />;

  return (
    <div className="admin-card-thumb">
      <img src={apresentacao.arquivo} alt={titulo || "Miniatura"} />
    </div>
  );
}

const categorias = [
  { tipo: "RV", titulo: "Realidade Virtual", classe: "rv" },
  { tipo: "RA", titulo: "Realidade Aumentada", classe: "ra" },
  { tipo: "Video 360", titulo: "Vídeo 360", classe: "v360" },
  { tipo: "Video 360i", titulo: "Vídeo 360i", classe: "v360i" },
];

function dataOrdenacao(item) {
  const data = item.created_at ? new Date(item.created_at).getTime() : 0;
  if (Number.isFinite(data) && data > 0) return data;
  return Number(item.originalId || item.id || 0);
}

function tagCorrespondeBusca(tag, termo) {
  return normalizarTextoBusca(tag).includes(termo);
}

function itemCorrespondeBuscaAdmin(item, termo) {
  return (
    normalizarTextoBusca(item?.titulo).includes(termo) ||
    normalizarTextoBusca(item?.descricao).includes(termo) ||
    normalizarTextoBusca(item?.info).includes(termo) ||
    (Array.isArray(item?.tags) && item.tags.some((tag) => tagCorrespondeBusca(tag, termo)))
  );
}

function formatarData(data) {
  if (!data) return "Inclusao recente";
  const parsed = new Date(data);
  if (Number.isNaN(parsed.getTime())) return "Inclusao recente";

  return parsed.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}


function Admin() {
  const { logout, usuario } = useAuth();
  const navigate = useNavigate();
  const usuarioEhIted = usuario?.is_admin && usuario?.login === "ited";

  
  const carouselRefs = useRef({});
const [itens, setItens] = useState([]);
  const [busca, setBusca] = useState("");
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [carregando, setCarregando] = useState(false);

  async function carregarItens() {
    setCarregando(true);
    try {
      const response = await api.get("/cards");
      const normalizados = (response.data || [])
        .map((card) => normalizarProjetoBackend(card, { admin: true }))
        .sort((a, b) => dataOrdenacao(b) - dataOrdenacao(a));

      setItens(normalizados);
      setErro("");
    } catch (error) {
      console.error("Erro ao carregar itens:", error);
      setErro("Nao foi possivel carregar os cards cadastrados.");
    } finally {
      setCarregando(false);
    }
  }

  useEffect(function () {
    carregarItens();
  }, []);

  const itensFiltrados = useMemo(function () {
    const termo = normalizarTextoBusca(busca);
    if (!termo) {
      return itens.map((item) => ({ ...item, relacionadoBusca: false }));
    }

    return itens
      .filter((item) => itemCorrespondeBuscaAdmin(item, termo))
      .sort((a, b) => dataOrdenacao(b) - dataOrdenacao(a))
      .map((item) => ({ ...item, relacionadoBusca: false }));
  }, [busca, itens]);

  const grupos = useMemo(function () {
    return categorias.map((categoria) => ({
      ...categoria,
      itens: itensFiltrados.filter((item) => obterTipoProjeto(item) === categoria.tipo),
    }));
  }, [itensFiltrados]);

  async function deletarItem(item) {
    try {
      await api.delete(`/cards/${item.originalId || item.id}`);
      setItens((prev) => prev.filter((atual) => atual.id !== item.id));
      setSucesso(`Card "${item.titulo}" deletado com sucesso!`);
      setTimeout(() => setSucesso(""), 4000);
    } catch (error) {
      console.error("Erro ao deletar item:", error);
      setErro(`Erro ao deletar: ${error.response?.data?.message || error.message}`);
      setTimeout(() => setErro(""), 5000);
    }
  }

  function handleLogout() {
    logout();
    navigate("/");
  }

  
  function rolarCategoria(tipo, direcao) {
    const container = carouselRefs.current[tipo];
    if (!container) return;

    const deslocamento = Math.max(container.clientWidth * 0.9, 320);
    container.scrollBy({
      left: direcao === "direita" ? deslocamento : -deslocamento,
      behavior: "smooth",
    });
  }
const totalFiltrado = itensFiltrados.length;

  return (
    <div className="admin-container admin-list-container">
      <div className="admin-content admin-list-page">
        {sucesso && <div className="notification success">{sucesso}</div>}
        {erro && <div className="notification error">{erro}</div>}

        <div className="admin-page-header admin-dashboard-header">
          <div>
            <span className="admin-eyebrow">Admin</span>
            <h1>Cards adicionados</h1>
          </div>
          <div className="admin-header-actions">
            <button type="button" className="admin-primary-action" onClick={() => navigate("/admin/novo")}>
              Novo card
            </button>
            <button type="button" className="admin-secondary-action" onClick={() => navigate("/admin/relatorios")}>
              Relatorios
            </button>
            {usuarioEhIted && (
              <button type="button" className="admin-secondary-action" onClick={() => navigate("/admin/usuarios/novo")}>
                Novo usuario
              </button>
            )}
            <button type="button" className="btn-voltar" onClick={() => navigate("/")}>
              Pagina inicial
            </button>
          </div>
        </div>

        <div className="admin-toolbar">
          <div className="admin-search-box">
            <span>Buscar</span>
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Busque por nome, tipo ou tag"
            />
          </div>
          <div className="admin-total-box">
            <strong>{totalFiltrado}</strong>
            <span>{busca.trim() ? "resultado(s)" : "card(s)"}</span>
          </div>
        </div>

        {carregando ? (
          <div className="admin-empty-state">Carregando cards...</div>
        ) : itens.length === 0 ? (
          <div className="admin-empty-state">
            <strong>Nenhum card cadastrado ainda.</strong>
            <button type="button" className="admin-primary-action" onClick={() => navigate("/admin/novo")}>
              Criar primeiro card
            </button>
          </div>
        ) : totalFiltrado === 0 ? (
          <div className="admin-empty-state">
            <strong>Nenhum card encontrado.</strong>
            <span>Tente buscar por outro nome ou tag.</span>
          </div>
        ) : (
          <div className="admin-category-grid">
            {grupos.map((grupo) => (
              grupo.itens.length > 0 && (
                <section className={`admin-category-section ${grupo.classe}`} key={grupo.tipo}>
                  <div className="admin-category-title">
                    <div>
                      <span>{grupo.itens.length}</span>
                      <h2>{grupo.titulo}</h2>
                    </div>
                  </div>

                  <div className="admin-carousel-wrapper">
                    {grupo.itens.length > 3 && (
                      <button
                        type="button"
                        className="admin-carousel-btn left"
                        onClick={() => rolarCategoria(grupo.tipo, "esquerda")}
                        aria-label={`Ver cards anteriores de ${grupo.titulo}`}
                      >
                        &#10094;
                      </button>
                    )}

                    <div
                      className="admin-card-grid"
                      ref={(element) => {
                        carouselRefs.current[grupo.tipo] = element;
                      }}
                    >
                      {grupo.itens.map((item) => (
                        <article className="admin-project-card" key={item.id}>
                          <AdminPreview apresentacao={item.apresentacao} titulo={item.titulo} />

                          <div className="admin-project-body">
                            <div className="admin-project-meta">
                              <span className="admin-type-pill">{labelTipo(item.tipo)}</span>
                              {item.relacionadoBusca && <span className="admin-related-pill">Relacionado</span>}
                            </div>

                            <h3>{item.titulo}</h3>
                            <p>{item.descricao}</p>

                            <div className="admin-card-tags">
                              {(item.tags || []).slice(0, 5).map((tag) => (
                                <span key={tag}>{tag}</span>
                              ))}
                            </div>

                            <div className="admin-project-footer">
                              <span>{formatarData(item.created_at)}</span>
                              <div className="item-actions">
                                <button
                                  className="btn-editar"
                                  onClick={() => navigate(`/admin/editar/${item.originalId || item.id}`)}
                                  title="Editar item"
                                >
                                  Editar
                                </button>
                                <button
                                  className="btn-deletar"
                                  onClick={() => {
                                    if (window.confirm(`Tem certeza que deseja deletar "${item.titulo}"?`)) {
                                      deletarItem(item);
                                    }
                                  }}
                                  title="Deletar item"
                                >
                                  Deletar
                                </button>
                              </div>
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>

                    {grupo.itens.length > 3 && (
                      <button
                        type="button"
                        className="admin-carousel-btn right"
                        onClick={() => rolarCategoria(grupo.tipo, "direita")}
                        aria-label={`Ver mais cards de ${grupo.titulo}`}
                      >
                        &#10095;
                      </button>
                    )}
                  </div>
                </section>
              )
            ))}
          </div>
        )}

        <div className="admin-actions">
          {/* <button onClick={() => navigate("/")} className="btn-voltar">Voltar a Pagina Inicial</button> */}
          <button onClick={handleLogout} className="btn-sair">Fazer Logout</button>
        </div>
      </div>
    </div>
  );
}

export default Admin;
